// audioDiagTick — CU-869dafa5q (codex review item #4 + diagnostic minset).
//
// Periodic snapshot of the receive-side audio pipeline state while a call
// is active. Designed so a future investigator can tell from logs alone WHY
// a backgrounded-tab call chopped: context suspended, RTP stopped, RTP
// arriving but element clock frozen, track muted/ended, etc.
//
// Interval is adaptive — 1 s while the document is hidden (where the chop
// reports come from), 2 s while visible (to keep log volume reasonable on
// normal happy-path calls).
//
// One tick is active at a time (per-page singleton). Re-INVITE / ICE
// restart calls `stopAudioDiagTick()` then `startAudioDiagTick()` again
// with the fresh pc; the singleton guarantees no orphans.

import logger from '../services/logger';
import {getActiveAudioContext} from './backgroundedAudioWorklet';
import {bumpDiagCounter, snapshotDiagCounters, resetDiagCounters, isAudioDiagEnabled, shouldLogDiagDisabledOnce} from './audioDiagCounters';

interface DiagTickParams {
    callId: string;
    pc: RTCPeerConnection;
    audioElement: HTMLAudioElement;
}

interface PrevSample {
    tsWallMs: number;
    tsPerfMs: number;
    audioContextTime: number | null;
    audioElCurrentTime: number;
    inboundPacketsReceived: number | null;
    inboundBytesReceived: number | null;
}

interface RtcStatsSnapshot {
    inboundPacketsReceived: number | null;
    inboundBytesReceived: number | null;
    inboundPacketsLost: number | null;
    inboundJitterMs: number | null;
    inboundJitterBufferMs: number | null;
    inboundAudioLevel: number | null;
    inboundConcealedSamples: number | null;
    inboundConcealmentEvents: number | null;
}

// Codex review — the active tick is identified by callId, not just by
// "whichever was started most recently". cleanupAudio() for an old session
// can race past a newer call's setupAudioStream(); without ownership keying,
// the old teardown would silently stop the new call's tick. Always pass
// callId to stopAudioDiagTick() so this can't happen.
type StopReason = 'call_end' | 'restart';
interface ActiveTick {
    callId: string;
    stop: (reason: StopReason) => void;
}
let activeTick: ActiveTick | null = null;

// Codex review — chop state must persist across a same-call restart so
// a chop that's already classified before a re-INVITE/ICE restart still
// gets its matching chop_end summary on recovery (rather than being
// silently dropped, or duplicating chop_start). On a different-callId
// start or a call_end stop we clear it.
interface PersistedChopState {
    callId: string;
    consecutiveStalls: number;
    lastClassification: string | null;
}
let persistedChopState: PersistedChopState | null = null;

async function snapshotInboundAudio(pc: RTCPeerConnection): Promise<RtcStatsSnapshot> {
    const snap: RtcStatsSnapshot = {
        inboundPacketsReceived: null, inboundBytesReceived: null,
        inboundPacketsLost: null, inboundJitterMs: null,
        inboundJitterBufferMs: null, inboundAudioLevel: null,
        inboundConcealedSamples: null, inboundConcealmentEvents: null,
    };
    try {
        const stats = await pc.getStats();
        stats.forEach((r: unknown) => {
            const report = r as Record<string, unknown>;
            if (report.type === 'inbound-rtp' && report.kind === 'audio') {
                if (typeof report.packetsReceived === 'number') snap.inboundPacketsReceived = report.packetsReceived;
                if (typeof report.bytesReceived === 'number') snap.inboundBytesReceived = report.bytesReceived;
                if (typeof report.packetsLost === 'number') snap.inboundPacketsLost = report.packetsLost;
                if (typeof report.jitter === 'number') snap.inboundJitterMs = report.jitter * 1000;
                if (typeof report.audioLevel === 'number') snap.inboundAudioLevel = report.audioLevel;
                if (typeof report.jitterBufferDelay === 'number'
                    && typeof report.jitterBufferEmittedCount === 'number'
                    && report.jitterBufferEmittedCount > 0) {
                    snap.inboundJitterBufferMs = (report.jitterBufferDelay / report.jitterBufferEmittedCount) * 1000;
                }
                if (typeof report.concealedSamples === 'number') snap.inboundConcealedSamples = report.concealedSamples;
                if (typeof report.concealmentEvents === 'number') snap.inboundConcealmentEvents = report.concealmentEvents;
            }
        });
    } catch {
        // getStats throws on a closed peer connection — we'll log the
        // tick anyway with null inbound fields so the investigator can
        // see the timing.
    }
    return snap;
}

export function startAudioDiagTick(params: DiagTickParams): () => void {
    // Kill switch — if disabled (?diag=off or localStorage phq_audio_diag=off)
    // return a no-op stop function. No tick scheduled, no getStats polling,
    // no log emit. CORE play()/worklet/SDP behavior is unaffected. Log
    // once per page so a thin log stream is attributable.
    if (!isAudioDiagEnabled()) {
        if (shouldLogDiagDisabledOnce()) {
            logger.info('Audio diagnostics disabled', {
                source: 'sip', event: 'audio_diagnostics_disabled',
                callId: params.callId,
            });
        }
        return () => { /* no-op */ };
    }
    // Stop any tick already running. When this is a re-INVITE / ICE restart
    // for the SAME callId, stopping and re-starting is the intended path
    // (fresh pc reference) — pass 'restart' so the previous stop() doesn't
    // emit a misleading call_end anomaly_summary or reset counters mid-call.
    // When it's a different callId, log it so a leak in the caller's
    // cleanup ordering is visible from logs alone; that's a true 'call_end'.
    //
    // Codex review — callId === 'unknown' is a fallback sipService uses
    // when session.id is missing. We CANNOT treat two 'unknown' calls as
    // the same call, so force a fresh-call path in that case (different
    // calls get full reset, even though the literal callIds match).
    let isFreshCall = true;
    if (activeTick) {
        const sameCall = activeTick.callId === params.callId && params.callId !== 'unknown';
        if (!sameCall) {
            logger.warn('Audio diag tick: replacing tick for different callId', {
                source: 'sip', event: 'audio_diag_tick_replaced',
                previousCallId: activeTick.callId,
                newCallId: params.callId,
            });
            activeTick.stop('call_end');
        } else {
            // Same-callId restart (re-INVITE / ICE restart). Don't fire
            // call_end summary, don't reset counters — the anomaly window
            // continues across the restart.
            isFreshCall = false;
            activeTick.stop('restart');
        }
    }
    // Reset counters at the start of a NEW call (not a same-call restart).
    // Codex flagged that the counters are page-scoped and would otherwise
    // include events that happened before the call started — making the
    // first anomaly summary materially misleading.
    if (isFreshCall) {
        resetDiagCounters();
        persistedChopState = null;
    }
    // Adopt or initialise chop-state persistence keyed by callId.
    if (!persistedChopState || persistedChopState.callId !== params.callId) {
        persistedChopState = {callId: params.callId, consecutiveStalls: 0, lastClassification: null};
    }
    const chopState = persistedChopState;

    let prev: PrevSample | null = null;
    let sampleSeq = 0;
    let stopped = false;
    let timerId: ReturnType<typeof setTimeout> | null = null;
    // Codex max-diag pass — derived gap detectors. Carry just enough state
    // across ticks to emit on transition (open/recover), not every tick.
    let scheduledTickAtPerfMs: number | null = null;
    let scheduledTickIntervalMs: number | null = null;
    let inContextClockGap = false;
    let contextClockGapCount = 0;
    let inMainThreadTimerGap = false;
    let mainThreadTimerGapCount = 0;

    const tick = async () => {
        if (stopped) return;
        sampleSeq++;
        const tsWallMs = Date.now();
        const tsPerfMs = performance.now();

        // audio_main_thread_timer_gap — compare the actual setTimeout
        // resolution vs. the scheduled interval. Chrome aggressively
        // throttles setTimeout in hidden tabs (1/min when intensive
        // throttling kicks in), so a 1 s timer firing 4 s late is direct
        // evidence the bg-tab clamp is active.
        if (scheduledTickAtPerfMs != null && scheduledTickIntervalMs != null) {
            const actualDelayMs = tsPerfMs - scheduledTickAtPerfMs;
            const lagMs = actualDelayMs - scheduledTickIntervalMs;
            const isGap = lagMs > Math.max(500, scheduledTickIntervalMs);
            if (isGap && !inMainThreadTimerGap) {
                inMainThreadTimerGap = true;
                mainThreadTimerGapCount++;
                bumpDiagCounter('mainThreadTimerGap');
                logger.warn('Main thread timer gap', {
                    source: 'sip', event: 'audio_main_thread_timer_gap',
                    callId: params.callId,
                    expectedDelayMs: scheduledTickIntervalMs,
                    actualDelayMs, lagMs,
                    visibilityStateAtFire: document.visibilityState,
                    gapCount: mainThreadTimerGapCount,
                });
            } else if (!isGap && inMainThreadTimerGap) {
                inMainThreadTimerGap = false;
                logger.info('Main thread timer gap recovered', {
                    source: 'sip', event: 'audio_main_thread_timer_gap_recovered',
                    callId: params.callId,
                    actualDelayMs, lagMs,
                    visibilityStateAtFire: document.visibilityState,
                });
            }
        }
        const ctx = getActiveAudioContext();
        const contextState = ctx?.state ?? null;
        const audioContextCurrentTime = ctx ? ctx.currentTime : null;
        const audioContextDeltaMs = prev?.audioContextTime != null && audioContextCurrentTime != null
            ? (audioContextCurrentTime - prev.audioContextTime) * 1000
            : null;

        const audioEl = params.audioElement;
        const audioElCurrentTime = audioEl.currentTime;
        const audioElDeltaMs = prev != null ? (audioElCurrentTime - prev.audioElCurrentTime) * 1000 : null;
        const audioElPlayedSeconds = audioEl.played?.length
            ? audioEl.played.end(audioEl.played.length - 1)
            : 0;

        const remoteAudioTrack = params.pc.getReceivers().find(r => r.track?.kind === 'audio')?.track ?? null;
        const inbound = await snapshotInboundAudio(params.pc);
        if (stopped) return;

        const inboundPacketsDelta = prev?.inboundPacketsReceived != null && inbound.inboundPacketsReceived != null
            ? inbound.inboundPacketsReceived - prev.inboundPacketsReceived
            : null;
        const inboundBytesDelta = prev?.inboundBytesReceived != null && inbound.inboundBytesReceived != null
            ? inbound.inboundBytesReceived - prev.inboundBytesReceived
            : null;
        const wallDeltaMs = prev != null ? tsWallMs - prev.tsWallMs : null;
        const perfDeltaMs = prev != null ? tsPerfMs - prev.tsPerfMs : null;

        // audio_context_clock_gap — if a worklet AudioContext is
        // attached and ctx.state==='running' but currentTime advanced
        // significantly less than wall/perf time did between ticks, the
        // audio render thread (or its clock source) stalled. Threshold
        // is conservative — half the expected delta — to avoid false
        // positives on legitimate small jitter. Open and recover events
        // bracket the suspect window.
        if (contextState === 'running' && audioContextDeltaMs != null
            && wallDeltaMs != null && wallDeltaMs > 0) {
            const expectedContextDeltaMs = wallDeltaMs;
            const ratio = audioContextDeltaMs / expectedContextDeltaMs;
            const isGap = ratio < 0.5 && (expectedContextDeltaMs - audioContextDeltaMs) > 250;
            if (isGap && !inContextClockGap) {
                inContextClockGap = true;
                contextClockGapCount++;
                bumpDiagCounter('contextClockGap');
                logger.warn('AudioContext clock gap', {
                    source: 'sip', event: 'audio_context_clock_gap',
                    callId: params.callId,
                    gapMs: expectedContextDeltaMs - audioContextDeltaMs,
                    wallDeltaMs, perfDeltaMs, contextDeltaMs: audioContextDeltaMs,
                    expectedContextDeltaMs, ratio,
                    gapCount: contextClockGapCount,
                    visibilityState: document.visibilityState,
                });
            } else if (!isGap && inContextClockGap) {
                inContextClockGap = false;
                logger.info('AudioContext clock gap recovered', {
                    source: 'sip', event: 'audio_context_clock_gap_recovered',
                    callId: params.callId,
                    wallDeltaMs, contextDeltaMs: audioContextDeltaMs, ratio,
                    visibilityState: document.visibilityState,
                });
            }
        }

        logger.info('Audio diagnostic tick', {
            source: 'sip', event: 'audio_diag_tick',
            callId: params.callId, sampleSeq,
            tsWallMs, tsPerfMs, wallDeltaMs,
            visibilityState: document.visibilityState,
            documentHidden: document.hidden,
            documentHasFocus: document.hasFocus(),
            contextState,
            audioContextCurrentTime, audioContextDeltaMs,
            audioContextBaseLatency: ctx?.baseLatency ?? null,
            audioContextOutputLatency: ctx?.outputLatency ?? null,
            audioElPaused: audioEl.paused,
            audioElMuted: audioEl.muted,
            audioElCurrentTime, audioElDeltaMs,
            audioElReadyState: audioEl.readyState,
            audioElNetworkState: audioEl.networkState,
            audioElPlayedSeconds,
            remoteTrackMuted: remoteAudioTrack?.muted ?? null,
            remoteTrackEnabled: remoteAudioTrack?.enabled ?? null,
            remoteTrackReadyState: remoteAudioTrack?.readyState ?? null,
            inboundPacketsReceived: inbound.inboundPacketsReceived,
            inboundBytesReceived: inbound.inboundBytesReceived,
            inboundPacketsDelta, inboundBytesDelta,
            inboundPacketsLost: inbound.inboundPacketsLost,
            inboundJitterMs: inbound.inboundJitterMs,
            inboundJitterBufferMs: inbound.inboundJitterBufferMs,
            inboundAudioLevel: inbound.inboundAudioLevel,
            inboundConcealedSamples: inbound.inboundConcealedSamples,
            inboundConcealmentEvents: inbound.inboundConcealmentEvents,
        });

        // Stall classification — RTP arriving from the network, audio
        // element not paused, but the element's playback clock didn't
        // advance. That's the smoking gun for "decoded audio is being
        // dropped between getStats and the speakers" — the failure mode
        // this whole ticket is trying to instrument.
        //
        // Codex review — the worklet AudioContext is OPTIONAL. The
        // non-worklet fallback path (feature flag off, browser unsupported,
        // or attachWorkletPath() failed) has `contextState === null`, and
        // we still want to detect stalls there. So: if a context exists,
        // it must be 'running'; if it doesn't, we don't require it.
        const contextIsHealthy = contextState === null || contextState === 'running';
        const isStallSample = inboundPacketsDelta != null && inboundPacketsDelta > 0
            && contextIsHealthy
            && !audioEl.paused
            && audioElDeltaMs != null && audioElDeltaMs < 100;

        if (isStallSample) {
            chopState.consecutiveStalls++;
            if (chopState.consecutiveStalls === 2) {
                logger.warn('Audio playout stall', {
                    source: 'sip', event: 'audio_playout_stall',
                    callId: params.callId,
                    audioElDeltaMs, audioElCurrentTime,
                    audioElReadyState: audioEl.readyState,
                    audioElNetworkState: audioEl.networkState,
                    inboundPacketsDelta, inboundBytesDelta,
                    contextState,
                    visibilityState: document.visibilityState,
                });
                const classification = 'inbound_rtp_present_but_playout_stalled';
                if (classification !== chopState.lastClassification) {
                    logger.warn('Audio chop classified', {
                        source: 'sip', event: 'audio_chop_classified',
                        callId: params.callId, classification, confidence: 'high',
                        contextState,
                        visibilityState: document.visibilityState,
                    });
                    // audio_anomaly_summary — codex Tier 3. Aggregates all
                    // the diagnostic counters that have fired since the
                    // last summary, giving an investigator a single
                    // payload that says "in the 30s before chop, we saw
                    // N long tasks, M worklet gaps, K device changes...".
                    // Reset after each fire so the counters become a
                    // window into the next anomaly.
                    const snap = snapshotDiagCounters();
                    logger.warn('Audio anomaly summary (chop start)', {
                        source: 'sip', event: 'audio_anomaly_summary',
                        callId: params.callId,
                        phase: 'chop_start',
                        classification,
                        ...snap,
                    });
                    resetDiagCounters();
                    chopState.lastClassification = classification;
                }
            }
        } else {
            // Codex review — "not a stall sample" must mean we have
            // EVIDENCE it's not a stall, not just absence of evidence.
            // The first post-restart tick has prev=null, so deltas are
            // null and isStallSample is false — but we DON'T know
            // whether playout recovered. Preserve chop state until we
            // get a real sample.
            const hasEvidence = inboundPacketsDelta != null && audioElDeltaMs != null;
            if (hasEvidence) {
                if (chopState.consecutiveStalls >= 2 && chopState.lastClassification != null) {
                    logger.info('Audio chop resolved', {
                        source: 'sip', event: 'audio_chop_resolved',
                        callId: params.callId,
                        previousClassification: chopState.lastClassification,
                    });
                    // audio_anomaly_summary at chop end — counts everything
                    // that happened DURING the chop window (since the start
                    // summary reset them).
                    const snap = snapshotDiagCounters();
                    logger.info('Audio anomaly summary (chop end)', {
                        source: 'sip', event: 'audio_anomaly_summary',
                        callId: params.callId,
                        phase: 'chop_end',
                        previousClassification: chopState.lastClassification,
                        ...snap,
                    });
                    resetDiagCounters();
                    chopState.lastClassification = null;
                }
                chopState.consecutiveStalls = 0;
            }
        }

        prev = {
            tsWallMs, tsPerfMs,
            audioContextTime: audioContextCurrentTime,
            audioElCurrentTime,
            inboundPacketsReceived: inbound.inboundPacketsReceived,
            inboundBytesReceived: inbound.inboundBytesReceived,
        };
        scheduleNext();
    };

    const scheduleNext = () => {
        if (stopped) return;
        const intervalMs = document.hidden ? 1000 : 2000;
        scheduledTickAtPerfMs = performance.now();
        scheduledTickIntervalMs = intervalMs;
        timerId = setTimeout(() => { void tick(); }, intervalMs);
    };

    logger.info('Audio diag tick started', {
        source: 'sip', event: 'audio_diag_tick_started',
        callId: params.callId,
    });
    scheduleNext();

    const stop = (reason: StopReason = 'call_end') => {
        if (stopped) return;
        stopped = true;
        if (timerId) clearTimeout(timerId);
        timerId = null;
        if (activeTick?.stop === stop) activeTick = null;
        // Final anomaly summary at call end only. For 'restart' (re-INVITE
        // / ICE restart for the SAME callId) skip both the summary and the
        // counter reset — the anomaly window continues into the new tick.
        if (reason === 'call_end') {
            const snap = snapshotDiagCounters();
            logger.info('Audio anomaly summary (call end)', {
                source: 'sip', event: 'audio_anomaly_summary',
                callId: params.callId,
                phase: 'call_end',
                ...snap,
            });
            resetDiagCounters();
            if (persistedChopState?.callId === params.callId) persistedChopState = null;
        }
        logger.info('Audio diag tick stopped', {
            source: 'sip', event: 'audio_diag_tick_stopped',
            callId: params.callId, sampleSeq, reason,
        });
    };
    activeTick = {callId: params.callId, stop};
    return () => stop('call_end');
}

/**
 * Stop the tick if (and only if) it currently belongs to `callId`. A
 * stale teardown (e.g. cleanupAudio() for an already-superseded session)
 * is a silent no-op rather than tearing down whichever tick happens to
 * be running. Use the no-op overload (callId omitted) for paths that
 * genuinely want to stop whatever's active — none in current code.
 */
export function stopAudioDiagTick(callId: string): void {
    if (!activeTick) return;
    if (activeTick.callId !== callId) {
        logger.info('Audio diag tick: stop request for non-active callId, ignoring', {
            source: 'sip', event: 'audio_diag_tick_stale_stop',
            requestedCallId: callId,
            activeCallId: activeTick.callId,
        });
        return;
    }
    activeTick.stop('call_end');
}
